<template>
  <main class="main weapon">
    <div class="f container">
      <LazyBreadcrumbs v-if="weapon" :item="weapon" :type="'weapon'" />
      <button :class="{compressed}"
              aria-label="compressed button"
              class="compress-btn ht hd"
              @click="toggleCompressed"
      >
        <svg>
          <use v-if="compressed" xlink:href="/icons/sprite.svg#grid-1"></use>
          <use v-else xlink:href="/icons/sprite.svg#grid-2"></use>
        </svg>
      </button>
    </div>
    <LazyPagesWeapon
        v-if="weapon && weapon.weapon !== 'Agents'"
        :filters="filters"
        :items="items"
        :pagination="{page, pagesLength}"
        :pending="pending"
        :weapon="weapon as Item & { category: string }"
        @on-page-change="onPageChange"
    />
    <div
        v-else-if="weapon"
        class="container"
    >
      <LazyPagesWeaponAgents
          :filters="filters"
          :items="items"
          :loading="pending"
          :pagesLength="pagesLength"
          :weapon="weapon as Item & { category: string }"
          @on-page-change="onPageChange"
      />
    </div>
  </main>
</template>

<script lang="ts" setup>
import {EventBus} from '~/composables/bus';
import {useStore} from "~/store";
import type {
  Item,
  ItemsByField,
  ItemWeaponPageFiltersResult
} from "~/graphql/generated/schema-types";
import {useDefineProduct} from "~/composables/define-product";
import {useAlternateLinks} from "~/composables/alternate-links";

const {locale, t} = useI18n()
const route = useRoute()
const router = useRouter();

const store = useStore()
const categories = computed(() => store.categories)
const {compressed, toggleCompressed} = useCompressed()
const {defineUseProduct} = useDefineProduct()

const items = ref<ItemsByField[]>([])
const pagesLength = ref(0)
const page = ref(parseInt(route.query.page as string) || 1);
const pending = ref(false)
const filters = ref({} as ItemWeaponPageFiltersResult)
const extra = ref<string | null>(route.query.extra as string || null)
const rarity = ref<string | null>(route.query.rarity as string || null)
const team = ref<string | null>(route.query.team as string || null)
const abortController = ref(new AbortController())

const weapon = computed(() => {
  let result = null

  categories.value.some((category) => {
    return category.items.some((item) => {
      if (route?.params?.id === getModifiedString(item?.weapon || '')) {
        result = {
          ...item,
          category: category.category
        }
        return true
      }
    })
  })

  return result as Item & {
    category: string
  } | null
})

const args = computed(() => {
  const args: any = {
    category: route.params.id as string,
    page: page.value || 1,
  };
  if (extra.value) args.extra = extra.value;
  if (rarity.value) args.rarity = rarity.value;
  if (team.value) args.team = team.value === 'terrorists' ? 'T' : 'CT';
  return args;
});

const getItems = async () => {
  if (pending.value) {
    abortController.value.abort()
    abortController.value = new AbortController();
  }
  pending.value = true

  try {
    const {
      data,
      errors
    } = await getWeaponItems(args.value, abortController.value?.signal)

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    return {
      items: data.itemsWeaponPage.result,
      pagesLength: data.itemsWeaponPage.max_pages || 0,
      filters: data.itemsWeaponPageFilters
    }
    // items.value = data.itemsWeaponPage.result
    // pagesLength.value = data.itemsWeaponPage.max_pages || 0
    // filters.value = data.itemsWeaponPageFilters

  } catch (e: any) {
    if (e.name !== 'AbortError') {
      EventBus.$emit('notify', {
        success: false,
        msg: e.message || 'An error occurred'
      })
    }

    console.log('getWeaponItems() error', e.message)
    // items.value = []
    // pagesLength.value = 0
    return {
      items: [] as ItemsByField[],
      pagesLength: 0,
      filters: {} as ItemWeaponPageFiltersResult
    }
  } finally {
    pending.value = false
  }
}

const {data, error} = await useAsyncData('weapon-items', getItems, {
  watch: [weapon],
  immediate: true
})

if (error.value || !weapon.value) {
  console.log(`data: ${data}, Weapons page fetch error: ${JSON.stringify(error)}, method: getItems, category = ${route.params.id as string}, page = ${page.value}, extra = ${extra.value}, rarity = ${rarity.value}, team = ${team.value}`)

  if (process.client) {
    window.location.reload();
  }
  console.log('Weapons page fetch error: ' + error.value?.message)
  throw createError({
    status: 404,
    statusText: 'Weapons page fetch error: ' + error.value?.message
  });
}

items.value = data.value?.items || []
pagesLength.value = data.value?.pagesLength || 0
filters.value = data.value?.filters || {}


const onPageChange = async (page: number) => {
  const queries = {
    ...route.query
  }
  if (page === 1) {
    delete queries.page
  } else {
    queries.page = page.toString()
  }

  await router.push({query: queries}).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      throw err;
    }
  });

  if (process.client) {
    window.scrollTo(0, 0)
  }
}

watch(() => route.query, async (query) => {
  const queries = {...query}
  const queryPage = parseInt(query.page as string);

  if (queryPage === 1 || queryPage === page.value) {
    delete queries.page //set page to 1
    await router.replace({query: queries})
  } else {
    page.value = parseInt(query.page as string) === page.value ? 1 : parseInt(query.page as string) || 1;
    rarity.value = query.rarity as string || null
    extra.value = query.extra as string || null
    team.value = query.team as string || null
    const res = await getItems();

    items.value = res?.items || []
    pagesLength.value = res?.pagesLength || 1
  }
});

const weaponTitle = computed(() => {
  // if (weapon.value?.category?.toLowerCase() === 'other') {
  return t('heads.weapon.title', {n: t('filters.weapon.' + weapon.value?.weapon)})
  // return t('all_items', {
  //   name: t('filters.weapon.' + weapon.value?.weapon),
  //   items: weapon.value.weapon === 'Case' || weapon.value.weapon === 'Crates' ? t('Skins') : t('items')
  // })
  // }
  //
  // if (weapon.value?.full_name.toLowerCase().includes('collect')) {
  //   return `${t('inspect_all_skins', {name: t('filters.weapon.' + weapon.value?.weapon)})}`
  // }
  // return `${t('all_skins', {name: t('filters.weapon.' + weapon.value?.weapon)})}`
})

const weaponDescription = computed(() => {
  if (weapon.value?.full_name.toLowerCase().includes('collect')) {
    return t('heads.collection.description', {name: t('filters.weapon.' + weapon.value?.weapon)})
  }
  return t('heads.item.description', {name: t('filters.weapon.' + weapon.value?.weapon)})
})

const weaponIcon = computed(() => {
  return getImageFromWaxpeer(weapon.value?.full_name || '')
})

onBeforeUnmount(() => {
  router.replace({query: {}})
})

const {alternateLinks, canonical} = useAlternateLinks(`/weapons/${route.params.id}`)

useHead({
  title: () => weaponTitle.value,
  meta: [
    {
      name: "keywords",
      content: "steam, counter strike global offensive, csgo, cs2, skins, items",
    },
  ],
  link: () => alternateLinks.value
})


useSeoMeta({
  title: () => weaponTitle.value,
  ogTitle: () => weaponTitle.value,
  description: () => weaponDescription.value,
  ogDescription: () => weaponDescription.value,
  ogImage: () => weaponIcon.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => weaponTitle.value,
  twitterDescription: () => weaponDescription.value,
  twitterImage: () => weaponIcon.value,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value
})

useSchemaOrg([
  defineItemList({
    id: `itemlist-${weaponTitle.value.replace(/\s+/g, '-').toLowerCase()}-weapon-page-${locale.value}`, // Уникальный ID для списка
    name: weaponTitle.value,
    description: weaponDescription.value,
    image: () => weaponIcon.value,
    itemListElement: await Promise.all(items.value.map(async (weapon, index) => {
      const product = await defineUseProduct(weapon as ItemsByField & {
        rating: number,
        votes: number
      });

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: product,
        id: product.id || `item-${index + 1}-${weapon?.market_hash_name}`,
      };
    })),
  }),
  defineBreadcrumb({
    itemListElement: [
      {name: () => t('main'), item: '/'},
      {name: weaponTitle.value, item: getWeaponLink(weapon.value?.full_name || '')},
    ],
  })
])

</script>

<style lang="scss" scoped>


.weapon {
  padding: 15rem 2rem 4rem;
  margin: 0 auto;
  width: 100%;

  .compress-btn {
    width: 40px;
    height: 40px;
    padding: 5px;
    border: 1px solid var(--gray);

    &.compressed {
      padding: 8px;
    }

    svg {
      width: 100%;
      height: 100%;
    }
  }

  @media screen and (max-width: 1115px) {
    padding-top: 9rem;
  }
  @media screen and (max-width: 979px) {
    padding-top: 7rem;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
